import { MAIN_MENU_SECTIONS, MenuItem, USER_MENU_SECTIONS } from '../../routes';

export type NavSearchItemKind = 'page' | 'event';

export interface FlatNavItem {
  label: string;
  link: string;
  icon?: string;
  /** Caminho para exibir no resultado da busca (ex.: Relatórios › Consulta). */
  path?: string;
  /** Linha secundária (ex.: placa · associado). */
  detail?: string;
  /** Etiqueta curta (ex.: etapa do evento). */
  badge?: string;
  kind?: NavSearchItemKind;
}

export interface NavSearchResultGroup {
  id: 'pages' | 'events';
  label: string;
  icon: string;
  items: FlatNavItem[];
}

function formatEtapaLabel(slug: string): string {
  return slug
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function mapEventoNavHitsToItems(hits: EventoNavSearchHit[]): FlatNavItem[] {
  return hits.map((hit) => {
    const detailParts: string[] = [];
    if (hit.placa) detailParts.push(hit.placa);
    if (hit.associado_nome) detailParts.push(hit.associado_nome);

    const ref = hit.uuid || String(hit.id);

    return {
      kind: 'event' as const,
      label: `Protocolo #${hit.numero}`,
      link: `/quadros/eventos?evento=${encodeURIComponent(ref)}`,
      icon: 'calendar',
      badge: hit.situacao ? formatEtapaLabel(hit.situacao) : undefined,
      detail: detailParts.length ? detailParts.join(' · ') : undefined,
    };
  });
}

export function buildNavSearchGroups(
  pages: FlatNavItem[],
  events: FlatNavItem[],
  pageLimit = 5,
  eventLimit = 5,
): NavSearchResultGroup[] {
  const groups: NavSearchResultGroup[] = [];
  const pageItems = pages.slice(0, pageLimit);
  const eventItems = events.slice(0, eventLimit);

  if (pageItems.length) {
    groups.push({
      id: 'pages',
      label: 'Páginas',
      icon: 'layout-dashboard',
      items: pageItems,
    });
  }

  if (eventItems.length) {
    groups.push({
      id: 'events',
      label: 'Eventos',
      icon: 'calendar',
      items: eventItems,
    });
  }

  return groups;
}

/** @deprecated Prefer `buildNavSearchGroups` para exibição agrupada. */
export function mergeNavSearchResults(
  pages: FlatNavItem[],
  events: FlatNavItem[],
  pageLimit = 5,
  eventLimit = 5,
): FlatNavItem[] {
  return [...pages.slice(0, pageLimit), ...events.slice(0, eventLimit)];
}

export interface EventoNavSearchHit {
  id: number;
  uuid: string;
  numero: string;
  situacao: string;
  placa?: string | null;
  associado_nome?: string | null;
}

export interface MenuSectionGroup {
  id: string;
  label: string;
  items: MenuItem[];
}

export interface UserMenuSectionGroup {
  id: string;
  label: string;
  entries: FlatNavItem[];
}

export function isUserMenuItem(item: MenuItem): boolean {
  return item.menuGroup === 'user';
}

export function isMainMenuItem(item: MenuItem): boolean {
  return !isUserMenuItem(item);
}

/** Achata itens com link, respeitando permissões via `canShow`. */
export function flattenNavItems(
  items: MenuItem[],
  canShow: (item: MenuItem) => boolean,
  parentPath?: string,
): FlatNavItem[] {
  const out: FlatNavItem[] = [];
  for (const item of items) {
    if (!canShow(item)) continue;
    const path = parentPath ? `${parentPath} › ${item.label}` : item.label;
    if (item.link) {
      out.push({ label: item.label, link: item.link, icon: item.icon, path: parentPath ? path : undefined });
    }
    if (item.children?.length) {
      out.push(...flattenNavItems(item.children, canShow, path));
    }
  }
  return out;
}

export function groupMainMenuItems(
  items: MenuItem[],
  canShow: (item: MenuItem) => boolean,
): MenuSectionGroup[] {
  const mainItems = items.filter(isMainMenuItem);

  return MAIN_MENU_SECTIONS
    .map((section) => ({
      id: section.id,
      label: section.label,
      items: mainItems.filter((item) => item.section === section.id && canShow(item)),
    }))
    .filter((group) => group.items.length > 0);
}

export function groupUserMenuItems(
  items: MenuItem[],
  canShow: (item: MenuItem) => boolean,
): UserMenuSectionGroup[] {
  const userItems = items.filter(isUserMenuItem);

  return USER_MENU_SECTIONS
    .map((section) => {
      const sectionItems = userItems.filter((item) => {
        if (item.userSection === section.id) return true;
        if (section.id === 'suporte' && item.children?.length && !item.userSection) {
          return true;
        }
        return false;
      });

      return {
        id: section.id,
        label: section.label,
        entries: flattenNavItems(sectionItems, canShow),
      };
    })
    .filter((group) => group.entries.length > 0);
}

export function routeMatchesMenuLink(url: string, link: string, exact = false): boolean {
  const path = url.split('?')[0].split('#')[0];
  if (exact) {
    return path === link || path === `${link}/`;
  }
  return path === link || path.startsWith(`${link}/`);
}

export function getExpandedGroupLabels(url: string, items: MenuItem[]): Set<string> {
  const expanded = new Set<string>();

  for (const item of items) {
    if (!item.children?.length) continue;

    const hasActiveChild = item.children.some((child) => {
      if (!child.link) return false;
      return routeMatchesMenuLink(url, child.link, child.exactActive ?? false);
    });

    if (hasActiveChild) {
      expanded.add(item.label);
    }
  }

  return expanded;
}

export function filterNavByQuery(items: FlatNavItem[], query: string, limit = 8): FlatNavItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return items
    .filter((item) => {
      const label = item.label.toLowerCase();
      const path = (item.path ?? '').toLowerCase();
      return label.includes(q) || path.includes(q);
    })
    .slice(0, limit);
}
